import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogTitle-root": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(2),
  },
}));

const countVars = (text) => {
  const matches = (text || "").match(/\{\{\s*\d+\s*\}\}/g) || [];
  const nums = matches.map((m) => parseInt(m.replace(/\D/g, ""), 10));
  return nums.length ? Math.max(...nums) : 0;
};

const SendTemplateModal = ({ open, onClose, template }) => {
  const [to, setTo] = useState("");
  const [params, setParams] = useState([]);
  const [sending, setSending] = useState(false);

  const bodyText = useMemo(() => {
    if (!template) return "";
    const comps = template.components || [];
    const body = comps.find((c) => c.type === "BODY");
    return body?.text || "";
  }, [template]);

  const varCount = countVars(bodyText);

  useEffect(() => {
    setTo("");
    setParams(Array(varCount).fill(""));
  }, [template, varCount]);

  const previewBody = useMemo(() => {
    let txt = bodyText;
    params.forEach((p, i) => {
      txt = txt.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"), p || `{{${i + 1}}}`);
    });
    return txt;
  }, [bodyText, params]);

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error("Informe o número de destino (com DDI/DDD).");
      return;
    }
    setSending(true);
    try {
      await api.post(`/metaTemplates/${template.id}/send`, {
        to: to.trim(),
        bodyParameters: params,
      });
      toast.success("Template enviado.");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  if (!template) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar template "{template.name}"</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Número de destino (ex: 5511999999999)"
          fullWidth
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value.replace(/\D/g, ""))}
          sx={{ mb: 2 }}
        />
        {varCount > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Variáveis
            </Typography>
            <Grid container spacing={1}>
              {params.map((p, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <TextField
                    label={`Variável {{${i + 1}}}`}
                    fullWidth
                    size="small"
                    value={p}
                    onChange={(e) => {
                      const next = [...params];
                      next[i] = e.target.value;
                      setParams(next);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#075e54" : "#dcf8c6",
            whiteSpace: "pre-wrap",
            fontSize: "0.875rem",
          }}
        >
          {previewBody || "(sem corpo)"}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          loading={sending}
          onClick={handleSend}
          color="primary"
          variant="contained"
        >
          Enviar
        </ButtonWithSpinner>
      </DialogActions>
    </StyledDialog>
  );
};

export default SendTemplateModal;
